// src/pages/ContactUs.jsx
import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import {
  doc, getDoc, collection, query, orderBy, getDocs, addDoc,
  serverTimestamp, onSnapshot, limit,
} from 'firebase/firestore';

/* ================================================================== */
/* Constants                                                           */
/* ================================================================== */

const FALLBACK_CONTACT = {
  contactInfo: {
    address:
      '3rd Floor, Room 811, Arab Frame Bldg.<br>2nd Street corner King Khalid St.<br>Al Khobar, Saudi Arabia',
    email: 'info@iiee-ercsa.org',
    phone: '+966 54 165 2438',
    officeHours: [
      { day: 'Friday', hours: '8:00 AM - 5:00 PM' },
      { day: 'Saturday', hours: '8:00 AM - 5:00 PM' },
      { day: 'Sunday - Thursday', hours: 'Closed' },
    ],
  },
  map: {
    address: 'Arab Frame Building, Al Khobar',
    street: '2nd Street corner King Khalid St.',
    note:
      'Located in the heart of Al Khobar, easily accessible from all major highways.',
    mapUrl: 'https://www.google.com/maps?q=Al+Khobar+Saudi+Arabia',
  },
  emergency: {
    phone: '+966 54 165 2438',
    note: 'Available 24/7 for emergencies only',
  },
};

const FALLBACK_SOCIAL = [
  { name: 'Facebook', icon: 'fab fa-facebook-f', url: 'https://www.facebook.com/groups/355271187923008' },
  { name: 'LinkedIn', icon: 'fab fa-linkedin-in', url: 'https://linkedin.com' },
  { name: 'Instagram', icon: 'fab fa-instagram', url: 'https://instagram.com' },
  { name: 'YouTube', icon: 'fab fa-youtube', url: 'https://youtube.com' },
  { name: 'Twitter', icon: 'fab fa-twitter', url: 'https://twitter.com' },
];

const FALLBACK_FAQS = [
  {
    id: '1',
    question: 'How can I become a member of IIEE-ERCSA?',
    answer:
      'You can become a member by clicking the "Login/Register" button on our website, then selecting "Register". Fill in your details, create a password, and submit. Once approved, you\'ll have access to member-only features.',
  },
  {
    id: '2',
    question: 'What are the benefits of membership?',
    answer:
      'Members enjoy access to exclusive training programs, CPD points, networking events with professional engineers, technical resources and publications, job opportunities, and discounted rates for seminars and workshops.',
  },
  {
    id: '3',
    question: 'How can I register for seminars?',
    answer:
      'Visit our Seminars page, browse upcoming programs, and click "Register Now" on any seminar you\'re interested in. You\'ll need to be logged in to your member account to register.',
  },
  {
    id: '4',
    question: 'How do I get CPE points?',
    answer:
      'CPE (Continuing Professional Education) points are awarded for attending our certified training programs, seminars, and workshops. Each program displays the number of CPE points you\'ll earn upon completion.',
  },
  {
    id: '5',
    question: 'Can I contribute to the forum?',
    answer:
      'Yes! Once you\'re logged in as a member, you can create new discussion threads, reply to existing ones, and share your technical knowledge with fellow engineers.',
  },
  {
    id: '6',
    question: 'How do I reset my password?',
    answer:
      'Click "Login" then "Forgot Password?" on the login page. Enter your registered email address, and we\'ll send you a password reset link.',
  },
];

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

const stripHtml = (html) => (html || '').replace(/<br\s*\/?>/gi, ', ').replace(/<[^>]*>/g, '').trim();

const sanitizePhone = (phone) => (phone || '').replace(/[^\d+]/g, '');

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || '');

/* ================================================================== */
/* Toast                                                               */
/* ================================================================== */

const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  const config =
    {
      success: { bg: 'bg-green-500', icon: 'fa-circle-check' },
      error: { bg: 'bg-red-500', icon: 'fa-circle-exclamation' },
      warning: { bg: 'bg-yellow-500', icon: 'fa-triangle-exclamation' },
      info: { bg: 'bg-blue-500', icon: 'fa-circle-info' },
    }[type] || { bg: 'bg-green-500', icon: 'fa-circle-check' };

  return (
    <motion.div
      role="status"
      aria-live="polite"
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 340, damping: 26 }}
      className={`fixed bottom-4 right-4 left-4 sm:left-auto z-[200] px-4 py-3 rounded-lg shadow-lg text-white flex items-center gap-2 sm:max-w-sm ${config.bg}`}
    >
      <i className={`fas ${config.icon}`} aria-hidden="true" />
      <span className="text-sm font-medium flex-1">{message}</span>
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        className="text-white/80 hover:text-white"
      >
        <i className="fas fa-times text-xs" aria-hidden="true" />
      </button>
    </motion.div>
  );
};

/* ================================================================== */
/* Skeleton                                                            */
/* ================================================================== */

const ContactSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 pt-28 pb-16">
    <div className="container mx-auto px-4 max-w-7xl">
      <div className="text-center mb-12">
        <div className="h-12 w-64 mx-auto bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-4" />
        <div className="h-4 w-96 max-w-full mx-auto bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6 text-center animate-pulse"
          >
            <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full mx-auto mb-4" />
            <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded mx-auto mb-3" />
            <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded mx-auto" />
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6 animate-pulse"
          >
            <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
            <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ================================================================== */
/* Main Component                                                      */
/* ================================================================== */

const ContactUs = () => {
  const { user, isAdmin } = useAuth();
  const [contactSettings, setContactSettings] = useState(null);
  const [socialLinks, setSocialLinks] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFaq, setActiveFaq] = useState(null);
  const [faqSearch, setFaqSearch] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);

  const formSectionRef = useRef(null);
  const nameInputRef = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  /* ================================================================ */
  /* Fetch data — live where it makes sense                            */
  /* ================================================================ */

  useEffect(() => {
    let unsubContact;
    let unsubSocial;
    let unsubFaqs;
    let cancelled = false;

    const loadAll = async () => {
      try {
        // Live contact settings
        unsubContact = onSnapshot(
          doc(db, 'settings', 'contact'),
          (snap) => {
            if (cancelled) return;
            setContactSettings(snap.exists() ? snap.data() : FALLBACK_CONTACT);
          },
          (err) => {
            console.warn('contact settings listener:', err);
            setContactSettings(FALLBACK_CONTACT);
          },
        );

        // Live social links
        unsubSocial = onSnapshot(
          doc(db, 'settings', 'social'),
          (snap) => {
            if (cancelled) return;
            const links = snap.exists() ? snap.data().links : null;
            setSocialLinks(links && links.length ? links : FALLBACK_SOCIAL);
          },
          (err) => {
            console.warn('social listener:', err);
            setSocialLinks(FALLBACK_SOCIAL);
          },
        );

        // FAQs — live too
        unsubFaqs = onSnapshot(
          query(collection(db, 'faqs'), orderBy('order', 'asc')),
          (snap) => {
            if (cancelled) return;
            if (snap.empty) {
              setFaqs(FALLBACK_FAQS);
              setActiveFaq(FALLBACK_FAQS[0]?.id ?? null);
            } else {
              const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
              setFaqs(list);
              setActiveFaq((prev) =>
                prev && list.some((f) => f.id === prev)
                  ? prev
                  : list[0]?.id ?? null,
              );
            }
          },
          (err) => {
            console.warn('faqs listener:', err);
            setFaqs(FALLBACK_FAQS);
            setActiveFaq(FALLBACK_FAQS[0]?.id ?? null);
          },
        );

        // Stop showing skeleton once contact settings arrive (or fail)
        // — use a small timeout as fallback since listeners may not fire instantly.
        setTimeout(() => {
          if (!cancelled) setLoading(false);
        }, 400);
      } catch (err) {
        console.error('Error loading contact data:', err);
        if (!cancelled) {
          setContactSettings(FALLBACK_CONTACT);
          setSocialLinks(FALLBACK_SOCIAL);
          setFaqs(FALLBACK_FAQS);
          setLoading(false);
        }
      }
    };

    loadAll();

    return () => {
      cancelled = true;
      unsubContact?.();
      unsubSocial?.();
      unsubFaqs?.();
    };
  }, []);

  /* ================================================================ */
  /* Contact form                                                      */
  /* ================================================================ */

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Please enter your name.';
    if (!formData.email.trim()) errors.email = 'Please enter your email.';
    else if (!isValidEmail(formData.email))
      errors.email = 'Please enter a valid email address.';
    if (!formData.message.trim())
      errors.message = 'Please enter your message.';
    else if (formData.message.trim().length < 10)
      errors.message = 'Message is a bit too short.';
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      showToast('Please fix the highlighted fields.', 'warning');
      // Focus the first errored field
      if (errors.name) nameInputRef.current?.focus();
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'contacts'), {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        subject: formData.subject.trim() || 'No subject',
        message: formData.message.trim(),
        status: 'unread',
        createdAt: serverTimestamp(),
        userId: user?.uid || null,
      });

      showToast(
        "Message sent successfully! We'll get back to you soon.",
        'success',
      );
      setFormData({ name: '', email: '', subject: '', message: '' });
      formSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      showToast('Error sending message. Please try again later.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  /* ================================================================ */
  /* Copy helper — works on desktop & mobile, HTTPS-aware             */
  /* ================================================================ */

  const handleCopy = useCallback(
    async (text, field) => {
      const value = (text || '').toString();
      if (!value) return;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
        } else {
          // Legacy fallback for http:// or older browsers
          const ta = document.createElement('textarea');
          ta.value = value;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        setCopiedField(field);
        showToast('Copied to clipboard', 'success');
        setTimeout(() => setCopiedField(null), 2000);
      } catch (err) {
        console.warn('Copy failed:', err);
        showToast('Could not copy. Please copy manually.', 'warning');
      }
    },
    [showToast],
  );

  /* ================================================================ */
  /* Newsletter                                                        */
  /* ================================================================ */

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(newsletterEmail)) {
      showToast('Please enter a valid email address.', 'warning');
      return;
    }
    setNewsletterSubmitting(true);
    try {
      await addDoc(collection(db, 'newsletter'), {
        email: newsletterEmail.trim().toLowerCase(),
        subscribedAt: serverTimestamp(),
        source: 'contact-page',
        userId: user?.uid || null,
      });
      showToast('Subscribed! Check your inbox for confirmation.', 'success');
      setNewsletterEmail('');
    } catch (err) {
      console.error(err);
      showToast('Subscription failed. Please try again.', 'error');
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  /* ================================================================ */
  /* Derived                                                           */
  /* ================================================================ */

  const { contactInfo = {}, map = {}, emergency = {} } = useMemo(
    () => contactSettings || FALLBACK_CONTACT,
    [contactSettings],
  );

  const filteredFaqs = useMemo(() => {
    const q = faqSearch.trim().toLowerCase();
    if (!q) return faqs;
    return faqs.filter(
      (f) =>
        (f.question || '').toLowerCase().includes(q) ||
        (f.answer || '').toLowerCase().includes(q),
    );
  }, [faqs, faqSearch]);

  if (loading) return <ContactSkeleton />;

  /* ================================================================ */
  /* Render                                                            */
  /* ================================================================ */

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 pt-28 pb-24 md:pb-16">
      <AnimatePresence>
        {toast && (
          <Toast
            key="toast"
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </AnimatePresence>

      <div className="container mx-auto px-4 max-w-7xl">
        {/* Hero */}
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-gray-800 dark:text-white mb-4"
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
          >
            Get in touch with IIEE-ERCSA. We're here to help and answer any
            questions you may have.
          </motion.p>
        </div>

        {/* Admin notice */}
        {isAdmin && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-orange/10 border border-orange rounded-xl p-3 text-center mb-8 text-sm"
          >
            <i className="fas fa-edit mr-1" aria-hidden="true" />
            <strong>Admin Mode:</strong> You can edit contact information in the{' '}
            <a
              href="/admin?tab=site"
              className="text-orange font-semibold hover:underline mx-1"
            >
              Admin Panel → Site Settings
            </a>
          </motion.div>
        )}

        {/* ==================================================== */}
        {/* Contact Info Cards                                    */}
        {/* ==================================================== */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
          {/* Visit */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6 text-center hover:shadow-xl transition-all group"
          >
            <div className="w-16 h-16 bg-orange/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-orange/20 transition">
              <i className="fas fa-map-pin text-2xl text-orange" aria-hidden="true" />
            </div>
            <h3 className="font-bold text-gray-800 dark:text-white mb-2">
              Visit Us
            </h3>
            <p
              className="text-gray-600 dark:text-gray-300 text-sm"
              dangerouslySetInnerHTML={{
                __html:
                  contactInfo.address ||
                  FALLBACK_CONTACT.contactInfo.address,
              }}
            />
            <button
              type="button"
              onClick={() =>
                handleCopy(
                  stripHtml(contactInfo.address || FALLBACK_CONTACT.contactInfo.address),
                  'address',
                )
              }
              className="text-xs text-orange mt-2 md:opacity-0 md:group-hover:opacity-100 transition"
            >
              {copiedField === 'address' ? (
                <>
                  <i className="fas fa-check mr-1" aria-hidden="true" />
                  Copied!
                </>
              ) : (
                <>
                  <i className="fas fa-copy mr-1" aria-hidden="true" />
                  Copy Address
                </>
              )}
            </button>
          </motion.div>

          {/* Email */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6 text-center hover:shadow-xl transition-all group"
          >
            <div className="w-16 h-16 bg-orange/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-orange/20 transition">
              <i className="fas fa-envelope text-2xl text-orange" aria-hidden="true" />
            </div>
            <h3 className="font-bold text-gray-800 dark:text-white mb-2">
              Email Us
            </h3>
            <a
              href={`mailto:${contactInfo.email || FALLBACK_CONTACT.contactInfo.email}`}
              className="text-orange hover:underline break-all text-sm"
            >
              {contactInfo.email || FALLBACK_CONTACT.contactInfo.email}
            </a>
            <button
              type="button"
              onClick={() =>
                handleCopy(
                  contactInfo.email || FALLBACK_CONTACT.contactInfo.email,
                  'email',
                )
              }
              className="text-xs text-orange mt-2 md:opacity-0 md:group-hover:opacity-100 transition block mx-auto"
            >
              {copiedField === 'email' ? (
                <>
                  <i className="fas fa-check mr-1" aria-hidden="true" />
                  Copied!
                </>
              ) : (
                <>
                  <i className="fas fa-copy mr-1" aria-hidden="true" />
                  Copy Email
                </>
              )}
            </button>
            <p className="text-gray-500 dark:text-gray-400 text-xs mt-2">
              We'll respond within 24-48 hours
            </p>
          </motion.div>

          {/* Phone */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6 text-center hover:shadow-xl transition-all group"
          >
            <div className="w-16 h-16 bg-orange/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-orange/20 transition">
              <i className="fas fa-phone-alt text-2xl text-orange" aria-hidden="true" />
            </div>
            <h3 className="font-bold text-gray-800 dark:text-white mb-2">
              Call Us
            </h3>
            <a
              href={`tel:${sanitizePhone(
                contactInfo.phone || FALLBACK_CONTACT.contactInfo.phone,
              )}`}
              className="text-orange hover:underline text-lg font-medium"
            >
              {contactInfo.phone || FALLBACK_CONTACT.contactInfo.phone}
            </a>
            <button
              type="button"
              onClick={() =>
                handleCopy(
                  contactInfo.phone || FALLBACK_CONTACT.contactInfo.phone,
                  'phone',
                )
              }
              className="text-xs text-orange mt-2 md:opacity-0 md:group-hover:opacity-100 transition block mx-auto"
            >
              {copiedField === 'phone' ? (
                <>
                  <i className="fas fa-check mr-1" aria-hidden="true" />
                  Copied!
                </>
              ) : (
                <>
                  <i className="fas fa-copy mr-1" aria-hidden="true" />
                  Copy Number
                </>
              )}
            </button>
            <p className="text-gray-500 dark:text-gray-400 text-xs mt-2">
              Available during office hours
            </p>
          </motion.div>

          {/* Office Hours */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6 text-center hover:shadow-xl transition-all"
          >
            <div className="w-16 h-16 bg-orange/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-clock text-2xl text-orange" aria-hidden="true" />
            </div>
            <h3 className="font-bold text-gray-800 dark:text-white mb-2">
              Office Hours
            </h3>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
              {(contactInfo.officeHours || FALLBACK_CONTACT.contactInfo.officeHours).map(
                (item, idx) => (
                  <li key={idx} className="flex justify-between gap-4">
                    <span>{item.day}</span>
                    <span
                      className={item.hours === 'Closed' ? 'text-red-500' : ''}
                    >
                      {item.hours}
                    </span>
                  </li>
                ),
              )}
            </ul>
          </motion.div>
        </div>

        {/* ==================================================== */}
        {/* Contact Form & Location                               */}
        {/* ==================================================== */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-12">
          <motion.div
            id="contact-form"
            ref={formSectionRef}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6"
          >
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
              <i className="fas fa-paper-plane text-orange mr-2" aria-hidden="true" />
              Send us a Message
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-5 text-sm">
              Have questions? We'd love to hear from you. Fill out the form
              below and we'll get back to you as soon as possible.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label
                  htmlFor="contact-name"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  ref={nameInputRef}
                  id="contact-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Juan Dela Cruz"
                  autoComplete="name"
                  aria-invalid={!!fieldErrors.name}
                  className={`w-full p-2.5 border rounded-lg dark:bg-gray-700 dark:border-gray-600 focus:ring-2 focus:ring-orange focus:border-transparent transition outline-none ${
                    fieldErrors.name
                      ? 'border-red-400 dark:border-red-500'
                      : ''
                  }`}
                />
                {fieldErrors.name && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <i className="fas fa-exclamation-circle" aria-hidden="true" />
                    {fieldErrors.name}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="contact-email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                  autoComplete="email"
                  aria-invalid={!!fieldErrors.email}
                  className={`w-full p-2.5 border rounded-lg dark:bg-gray-700 dark:border-gray-600 focus:ring-2 focus:ring-orange focus:border-transparent transition outline-none ${
                    fieldErrors.email
                      ? 'border-red-400 dark:border-red-500'
                      : ''
                  }`}
                />
                {fieldErrors.email && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <i className="fas fa-exclamation-circle" aria-hidden="true" />
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="contact-subject"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Subject
                </label>
                <input
                  id="contact-subject"
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="What is this regarding?"
                  className="w-full p-2.5 border rounded-lg dark:bg-gray-700 dark:border-gray-600 focus:ring-2 focus:ring-orange focus:border-transparent transition outline-none"
                />
              </div>

              <div>
                <label
                  htmlFor="contact-message"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Please provide details about your inquiry..."
                  aria-invalid={!!fieldErrors.message}
                  className={`w-full p-2.5 border rounded-lg dark:bg-gray-700 dark:border-gray-600 focus:ring-2 focus:ring-orange focus:border-transparent transition resize-none outline-none ${
                    fieldErrors.message
                      ? 'border-red-400 dark:border-red-500'
                      : ''
                  }`}
                />
                {fieldErrors.message && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <i className="fas fa-exclamation-circle" aria-hidden="true" />
                    {fieldErrors.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-orange hover:bg-orange-dark text-white font-semibold px-6 py-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.99]"
              >
                {submitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2" aria-hidden="true" />
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane mr-2" aria-hidden="true" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6"
          >
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
              <i className="fas fa-map-marker-alt text-orange mr-2" aria-hidden="true" />
              Our Location
            </h2>

            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 text-center mb-4">
              <i className="fas fa-building text-3xl text-orange mb-3" aria-hidden="true" />
              <p className="text-gray-700 dark:text-gray-300 font-medium">
                {map.address || FALLBACK_CONTACT.map.address}
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                {map.street || FALLBACK_CONTACT.map.street}
              </p>
            </div>

            <div className="rounded-lg overflow-hidden mb-4 bg-gray-200 dark:bg-gray-700">
              <img
                src="https://placehold.co/600x300/f0f0f0/orange?text=Map+Location+-+Al+Khobar+Saudi+Arabia"
                alt="Map showing Al Khobar, Saudi Arabia"
                className="w-full h-48 object-cover"
                loading="lazy"
              />
            </div>

            <a
              href={map.mapUrl || FALLBACK_CONTACT.map.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center bg-orange/20 hover:bg-orange text-orange hover:text-white px-4 py-2.5 rounded-lg text-sm transition mb-3 active:scale-[0.99]"
            >
              <i className="fas fa-directions mr-1" aria-hidden="true" /> Open
              in Google Maps
            </a>

            <p className="text-sm text-gray-500 dark:text-gray-400">
              <i className="fas fa-info-circle text-orange mr-1" aria-hidden="true" />
              {map.note || FALLBACK_CONTACT.map.note}
            </p>
          </motion.div>
        </div>

        {/* ==================================================== */}
        {/* Social Links                                          */}
        {/* ==================================================== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6 mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 text-center">
            <i className="fas fa-share-alt text-orange mr-2" aria-hidden="true" />
            Connect With Us
          </h2>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            {socialLinks.map((link, idx) => (
              <motion.a
                key={idx}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.97 }}
                className="flex flex-col items-center gap-2 p-4 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-orange/20 transition min-w-[100px] group"
                aria-label={link.name}
              >
                <i
                  className={`${link.icon} text-3xl text-orange group-hover:scale-110 transition`}
                  aria-hidden="true"
                />
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {link.name}
                </span>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* ==================================================== */}
        {/* FAQ Section                                           */}
        {/* ==================================================== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6 mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 text-center">
            <i className="fas fa-question-circle text-orange mr-2" aria-hidden="true" />
            Frequently Asked Questions
          </h2>

          {faqs.length > 4 && (
            <div className="relative max-w-md mx-auto mb-6">
              <i
                className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={faqSearch}
                onChange={(e) => setFaqSearch(e.target.value)}
                aria-label="Search FAQs"
                className="w-full pl-11 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-full focus:outline-none focus:ring-2 focus:ring-orange dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
          )}

          {filteredFaqs.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8 text-sm">
              <i className="fas fa-search mr-1" aria-hidden="true" />
              No FAQs match your search.
            </p>
          ) : (
            <div className="space-y-3">
              {filteredFaqs.map((faq, idx) => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: Math.min(idx * 0.05, 0.3) }}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setActiveFaq((prev) => (prev === faq.id ? null : faq.id))
                    }
                    aria-expanded={activeFaq === faq.id}
                    className="w-full text-left p-4 font-semibold flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-700 transition gap-3"
                  >
                    <span className="text-gray-800 dark:text-white text-sm md:text-base">
                      {faq.question}
                    </span>
                    <motion.i
                      animate={{ rotate: activeFaq === faq.id ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      className="fas fa-chevron-down text-orange flex-shrink-0"
                      aria-hidden="true"
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {activeFaq === faq.id && (
                      <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <p className="px-4 pb-4 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                          {faq.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* ==================================================== */}
        {/* Emergency Contact                                     */}
        {/* ==================================================== */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6 }}
          className="bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-900/10 border border-red-300 dark:border-red-800 rounded-2xl p-6 md:p-8 text-center"
        >
          <div className="w-16 h-16 md:w-20 md:h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <i
              className="fas fa-exclamation-triangle text-3xl md:text-4xl text-red-500"
              aria-hidden="true"
            />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-red-600 dark:text-red-400 mb-2">
            Emergency Contact
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
            For urgent matters outside office hours
          </p>
          <a
            href={`tel:${sanitizePhone(
              emergency.phone || FALLBACK_CONTACT.emergency.phone,
            )}`}
            className="inline-flex items-center gap-3 text-2xl md:text-3xl font-bold text-red-600 dark:text-red-400 hover:underline active:scale-[0.98] transition"
          >
            <i className="fas fa-phone-alt text-xl md:text-2xl" aria-hidden="true" />
            {emergency.phone || FALLBACK_CONTACT.emergency.phone}
          </a>
          <div className="mt-4 p-3 bg-red-500/10 rounded-lg inline-block">
            <i className="fas fa-clock text-red-500 mr-2" aria-hidden="true" />
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {emergency.note || FALLBACK_CONTACT.emergency.note}
            </span>
          </div>
        </motion.div>

        {/* ==================================================== */}
        {/* Newsletter                                            */}
        {/* ==================================================== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 md:p-8 text-center text-white"
        >
          <h3 className="text-xl md:text-2xl font-bold mb-2">Stay Updated</h3>
          <p className="mb-4 opacity-90 text-sm md:text-base">
            Subscribe to our newsletter for the latest news, events, and
            updates
          </p>
          <form
            onSubmit={handleNewsletterSubmit}
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              placeholder="Your email address"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              aria-label="Email for newsletter"
              required
              className="flex-1 px-4 py-2.5 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-white"
            />
            <button
              type="submit"
              disabled={newsletterSubmitting}
              className="bg-white text-orange px-6 py-2.5 rounded-lg font-semibold hover:bg-gray-100 transition disabled:opacity-60 active:scale-[0.98]"
            >
              {newsletterSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-1" aria-hidden="true" />
                  Subscribing...
                </>
              ) : (
                'Subscribe'
              )}
            </button>
          </form>
        </motion.div>
      </div>

      {/* ==================================================== */}
      {/* Mobile sticky CTA — quick call / email                */}
      {/* ==================================================== */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg">
        <div className="grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-700">
          <a
            href={`tel:${sanitizePhone(
              contactInfo.phone || FALLBACK_CONTACT.contactInfo.phone,
            )}`}
            className="flex items-center justify-center gap-2 py-3 text-orange font-semibold active:bg-orange/10 transition"
          >
            <i className="fas fa-phone-alt" aria-hidden="true" />
            Call
          </a>
          <a
            href={`mailto:${contactInfo.email || FALLBACK_CONTACT.contactInfo.email}`}
            className="flex items-center justify-center gap-2 py-3 text-orange font-semibold active:bg-orange/10 transition"
          >
            <i className="fas fa-envelope" aria-hidden="true" />
            Email
          </a>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;